import {
  type AssignExamsByExamCourseCodesAndMonthOfYearMutationOptions,
  useAssignExamsByExamCourseCodesAndMonthOfYearMutation,
  useUnassignExamsMutation,
} from '@exam-center/graphql';
import { dayjs } from '@shared/utils';
import { createContext, useCallback, useMemo } from 'react';

import { useGetMonthlyExamDistributionData } from '../../hooks';

import type {
  ExamDistributionByMonthContextValue,
  ExamDistributionByMonthProviderProps,
  UnassignExamsMutationOptions,
} from './types';
import { calculateExamDistributionSummary, mapExamEventsAndCourses } from './utils';

const ExamDistributionByMonthContext = createContext<
  ExamDistributionByMonthContextValue | undefined
>(undefined);

export default function ExamDistributionByMonthProvider(
  props: Readonly<ExamDistributionByMonthProviderProps>,
) {
  const { year, month, examDate, examCenterCodes, provinceIds, children } = props;

  const examDistributionPeriod = dayjs()
    .year(year)
    .month(month - 1);

  const examStartedAt = examDate
    ? dayjs(examDate).startOf('day').toDate()
    : examDistributionPeriod.startOf('month').toDate();

  const examEndedAt = examDate
    ? dayjs(examDate).endOf('day').toDate()
    : examDistributionPeriod.endOf('month').toDate();

  const { data: examDistributionData, loading: isLoading } = useGetMonthlyExamDistributionData({
    examStartedAt,
    examEndedAt,
    examCenterCodes,
    provinceIds,
  });

  const [assignExamsByExamCourseCodesAndMonthOfYearMutation, { loading: isAssigningExams }] =
    useAssignExamsByExamCourseCodesAndMonthOfYearMutation();

  const [unassignExamsMutation, { loading: isUnAssigningExams }] = useUnassignExamsMutation();

  const examEventsWithCourses = useMemo(() => {
    return mapExamEventsAndCourses(
      examDistributionData.examEvents,
      examDistributionData.examEventCourses,
    );
  }, [examDistributionData]);

  const examDistributionSummaryData = useMemo(() => {
    return calculateExamDistributionSummary(examEventsWithCourses);
  }, [examEventsWithCourses]);

  const assignExamsByExamCourseCodesAndMonthOfYear = useCallback(
    async (
      examCourseCodes: string[],
      options?: {
        onCompleted?: AssignExamsByExamCourseCodesAndMonthOfYearMutationOptions['onCompleted'];
        onError?: AssignExamsByExamCourseCodesAndMonthOfYearMutationOptions['onError'];
      },
    ) => {
      const { onCompleted, onError } = options ?? {};
      await assignExamsByExamCourseCodesAndMonthOfYearMutation({
        variables: {
          assignExamsByExamCourseCodesAndMonthOfYearInput: {
            examCourseCodes,
            month,
            year,
          },
        },
        refetchQueries: ['getExamEventCoursesByExamEventIds'],
        awaitRefetchQueries: true,
        onCompleted,
        onError,
      });
    },
    [assignExamsByExamCourseCodesAndMonthOfYearMutation, month, year],
  );

  const unassignExams = useCallback(
    async (
      examCourseCodes: string[],
      options?: {
        onCompleted?: UnassignExamsMutationOptions['onCompleted'];
        onError?: UnassignExamsMutationOptions['onError'];
      },
    ) => {
      const { onCompleted, onError } = options ?? {};
      await unassignExamsMutation({
        variables: {
          unassignExamsInput: {
            examCourseCodes,
            month,
            year,
          },
        },
        refetchQueries: ['getExamEventCoursesByExamEventIds'],
        awaitRefetchQueries: true,
        onCompleted,
        onError,
      });
    },
    [unassignExamsMutation, month, year],
  );

  const isExamDistributionDataEmpty = useMemo(
    () =>
      !examDate &&
      examCenterCodes.length === 0 &&
      provinceIds.length === 0 &&
      examDistributionData.examEvents.length === 0,
    [examDate, examCenterCodes, provinceIds, examDistributionData],
  );

  const contextValue: ExamDistributionByMonthContextValue = useMemo(() => {
    return {
      year,
      month,
      examDistributionData,
      isLoading,
      isExamDistributionDataEmpty,
      examEventsWithCourses,
      examDistributionSummaryData,
      unassignExams,
      isUnAssigningExams,
      assignExamsByExamCourseCodesAndMonthOfYear,
      isAssigningExams,
    };
  }, [
    examDistributionData,
    examEventsWithCourses,
    examDistributionSummaryData,
    isLoading,
    isExamDistributionDataEmpty,
    month,
    year,
    unassignExams,
    isUnAssigningExams,
    assignExamsByExamCourseCodesAndMonthOfYear,
    isAssigningExams,
  ]);

  return (
    <ExamDistributionByMonthContext.Provider value={contextValue}>
      {children}
    </ExamDistributionByMonthContext.Provider>
  );
}

export { ExamDistributionByMonthContext };
